// ReportSettingsPanel.jsx — 资方进度报告的项目层设置（SB-202 S3；第三百一十三批从 AdminAccountsPage 抽出成共享件）。
//
// 为什么抽出来：需求方 2026-09-25 反馈「没在系统页面上找到调整报告相关项目级设置的地方」——设置对话框自第三百零八批
// 起就在 /admin「账号」页的项目组表里，但那一页的主题是账号，没人会到那里找「报告间隔」。于是同一只面板再挂到
// /admin/settings（「设置」页）顶部：两处共用同一份组件与同一条端点，改一处即两处同变。
//
// 五项：是否汇报、汇报间隔、汇报目标成员（邮箱；空 ⇒ 本组在用的资方账号）、排除版本、里程碑闸门。
// 三条刻意之处（沿第三百零八批）：① 只送改了的字段（diffOf）；② 校验在按下之前按格给出（parseForm）；
// ③ 启用前的「先试发给自己核格式」在命令行（`--remote --dry --to`），这里只提醒一句。
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Stack, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import api, { ApiError } from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import DataTable from './DataTable.jsx';
import { diffOf, formOf, parseForm, summaryOf } from '../pages/reportSettings.mjs';

// 契约 §4.4 写的是裸数组，本仓其他 admin 端点一律套一层——两种都收（与 AdminAccountsPage 同一条容错）。
export function groupsOf(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.projectGroups)) return data.projectGroups;
  if (Array.isArray(data?.groups)) return data.groups;
  return [];
}

export function groupErrMsg(e, t) {
  return t(errKey(e, {
    byCode: { IRREVERSIBLE: 'admin.groups.irreversible' },
    byStatus: { 403: 'admin.accounts.forbidden', 404: 'admin.groups.notFound' },
    fallback: 'admin.groups.error',
  }));
}

export function ReportSettingsDialog({ t, notify, group, onClose, onSaved }) {
  const [form, setForm] = useState(() => formOf(group.report));
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);
  const parsed = parseForm(form);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === 'enabled' ? e.target.checked : e.target.value }));
  const touch = (k) => () => setTouched((x) => ({ ...x, [k]: true }));
  const problem = (k) => (touched[k] && parsed.problems[k] ? t(parsed.problems[k]) : '');

  const save = async () => {
    setTouched({ everyDays: true, recipients: true, excludeVersions: true, gates: true });
    if (!parsed.ok) return;
    const diff = diffOf(group.report, parsed.body);
    if (Object.keys(diff).length === 0) { notify(t('admin.groups.report.unchanged'), 'info'); onClose(); return; }
    setBusy(true);
    try {
      await api.adminSetReportSettings(group.id, diff);
      notify(t('admin.groups.report.saved'), 'success');
      await onSaved();
    } catch (e) {
      notify(e instanceof ApiError && e.status === 400 ? t('admin.groups.report.err.rule') : groupErrMsg(e, t), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" data-report-dialog={group.slug}>
      <DialogTitle>{t('admin.groups.report.title', { slug: group.slug })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">{t('admin.groups.report.hint')}</Alert>
          {/* data-* 挂在组件根上（MUI 9 已无 inputProps 通路）；探针用「[data-report-x] input／textarea」取到真输入框。 */}
          <FormControlLabel
            control={<Checkbox checked={form.enabled} onChange={set('enabled')} data-report-enabled="1" />}
            label={t('admin.groups.report.enabled')} />
          <TextField size="small" type="number" label={t('admin.groups.report.everyDays')} value={form.everyDays}
                     onChange={set('everyDays')} onBlur={touch('everyDays')}
                     error={Boolean(problem('everyDays'))} helperText={problem('everyDays') || t('admin.groups.report.everyHelp')}
                     slotProps={{ htmlInput: { min: 1, max: 60 } }} data-report-every="1" />
          <TextField size="small" multiline minRows={2} label={t('admin.groups.report.recipients')} value={form.recipients}
                     onChange={set('recipients')} onBlur={touch('recipients')}
                     error={Boolean(problem('recipients'))} helperText={problem('recipients') || t('admin.groups.report.recipientsHelp')}
                     data-report-recipients="1" />
          <TextField size="small" label={t('admin.groups.report.exclude')} value={form.excludeVersions}
                     onChange={set('excludeVersions')} onBlur={touch('excludeVersions')}
                     error={Boolean(problem('excludeVersions'))} helperText={problem('excludeVersions') || t('admin.groups.report.excludeHelp')}
                     data-report-exclude="1" />
          <TextField size="small" multiline minRows={2} label={t('admin.groups.report.gates')} value={form.gates}
                     onChange={set('gates')} onBlur={touch('gates')}
                     error={Boolean(problem('gates'))} helperText={problem('gates') || t('admin.groups.report.gatesHelp')}
                     data-report-gates="1" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={busy || !parsed.ok} onClick={save} data-report-save="1">
          {t('admin.groups.report.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 独立面板：项目组 → 资方简报一览＋「设置」。挂在 /admin/settings 顶部（第三百一十三批）。
export default function ReportSettingsPanel({ t, lang, notify }) {
  const [state, setState] = useState({ status: 'loading', groups: [], error: '' });
  const [report, setReport] = useState(null);
  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const data = await api.adminProjectGroups();
      setState({ status: 'ready', groups: groupsOf(data), error: '' });
    } catch (e) {
      setState({ status: 'error', groups: [], error: groupErrMsg(e, t) });
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card variant="outlined" data-report-panel="1">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{t('admin.groups.report.panelTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('admin.groups.report.panelIntro')}</Typography>
        {state.status === 'loading' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={22} /></Box>
        ) : state.status === 'error' ? (
          <Box>
            <Alert severity="error" sx={{ mb: 1 }}>{state.error}</Alert>
            <Button size="small" onClick={load}>{t('common.retry')}</Button>
          </Box>
        ) : state.groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('admin.groups.empty')}</Typography>
        ) : (
          <DataTable minWidth={520}>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.groups.slug')}</TableCell>
                <TableCell>{t('admin.groups.name')}</TableCell>
                <TableCell>{t('admin.groups.report')}</TableCell>
                <TableCell align="right">{t('admin.accounts.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.groups.map((g) => {
                const s = summaryOf(g.report);
                return (
                  <TableRow key={g.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{g.slug}</TableCell>
                    <TableCell>{(lang === 'en' ? g.displayNameEn : g.displayName) || '—'}</TableCell>
                    <TableCell data-report-cell={g.slug}>
                      <Stack spacing={0.25}>
                        <Chip size="small" color={s.enabled ? 'success' : 'default'}
                              label={s.enabled ? t('admin.groups.report.on', { days: s.everyDays }) : t('admin.groups.report.off')} />
                        <Typography variant="caption" color="text.secondary">
                          {t('admin.groups.report.counts', { recipients: s.recipients, exclude: s.exclude, gates: s.gates })}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setReport(g)} data-report-edit={g.slug}>{t('admin.groups.report.edit')}</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </DataTable>
        )}
        {report ? (
          <ReportSettingsDialog t={t} notify={notify} group={report}
                                onClose={() => setReport(null)}
                                onSaved={async () => { setReport(null); await load(); }} />
        ) : null}
      </CardContent>
    </Card>
  );
}
